import { useState, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import ShopSyncAPI from '../../../services/api/integrations/shop-sync-api';
import Logger from '../../../utils/logger';

/**
 * Lists unresolved shop-sync exceptions for one project.
 * When canResolve is true (default), admins can resolve them.
 * Calls onResolved() after a successful resolve so the parent can refresh
 * badge counts.
 */
export const ShopExceptionsDialog = ({
  open,
  projectId,
  projectName,
  token,
  onClose,
  onResolved,
  canResolve = true,
}) => {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  // Id of the exception awaiting resolve confirmation (null = dialog closed).
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    if (!projectId || !token) return;
    setLoading(true);
    try {
      const data = await ShopSyncAPI.getExceptions(token, {
        resolved: false,
        projectId,
      });
      setExceptions(data);
    } catch (err) {
      Logger.error('Error loading shop-sync exceptions', err);
      setExceptions([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleResolve = async (id) => {
    setResolvingId(id);
    try {
      await ShopSyncAPI.resolveException(token, id);
      setExceptions((prev) => prev.filter((e) => e.id !== id));
      onResolved?.();
    } catch (err) {
      Logger.error('Error resolving exception', err);
      alert(err.response?.data?.message || 'Failed to resolve exception');
    } finally {
      setResolvingId(null);
      setConfirmId(null);
    }
  };

  const severityColor = (s) => (s === 'informational' ? 'info' : 'error');
  // pg returns numeric as a string ("8.00"); coerce to a number, blank if null.
  const fmtHours = (h) => (h === null || h === undefined ? '—' : Number(h));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { width: '95vw', maxWidth: '1600px' } }}
    >
      <DialogTitle>Shop Sync Exceptions — {projectName}</DialogTitle>
      <DialogContent dividers>
        {canResolve ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>How to clear an exception</AlertTitle>
            Work these in order — don&apos;t resolve-then-hope:
            <Box component="ol" sx={{ m: 0, mt: 0.5, pl: 3 }}>
              <li>
                <strong>Fix the underlying issue</strong> (e.g. the package /
                activity mapping or employee record the exception points to).
              </li>
              <li>
                <strong>Re-run a full sync</strong> so the corrected data is
                actually written into Fieldtime.
              </li>
              <li>
                <strong>Then mark it resolved</strong> here to clear it from the
                list.
              </li>
            </Box>
            Marking resolved on its own only hides the row — the sync re-raises
            it on the next run if the real problem still exists.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>View only</AlertTitle>
            These shop time sync exceptions need an administrator to fix and
            resolve. Contact an admin if hours look wrong for this project.
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : exceptions.length === 0 ? (
          <Typography sx={{ p: 2 }}>No unresolved exceptions. 🎉</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Severity</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Package</TableCell>
                <TableCell align="right">ST</TableCell>
                <TableCell align="right">OT</TableCell>
                <TableCell align="right">DT</TableCell>
                {canResolve && <TableCell align="center">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {exceptions.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>
                    <Chip
                      size="small"
                      label={e.severity}
                      color={severityColor(e.severity)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{e.exception_type}</TableCell>
                  <TableCell sx={{ maxWidth: 480 }}>{e.details}</TableCell>
                  <TableCell>
                    {e.legacy_date ? e.legacy_date.slice(0, 10) : '—'}
                  </TableCell>
                  <TableCell>{e.legacy_user_name || '—'}</TableCell>
                  <TableCell>
                    {e.legacy_package_name || e.legacy_package_id || '—'}
                  </TableCell>
                  <TableCell align="right">{fmtHours(e.hours_st)}</TableCell>
                  <TableCell align="right">{fmtHours(e.hours_ot)}</TableCell>
                  <TableCell align="right">{fmtHours(e.hours_dt)}</TableCell>
                  {canResolve && (
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={resolvingId === e.id}
                        onClick={() => setConfirmId(e.id)}
                      >
                        {resolvingId === e.id ? 'Resolving…' : 'Resolve'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {canResolve && (
        <Dialog
          open={confirmId !== null}
          onClose={() => setConfirmId(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Mark this exception resolved?</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              This does <strong>not</strong> fix the underlying error — it only
              marks the exception as resolved and removes it from the list. The
              sync will re-raise it on the next run if the real problem still
              exists.
            </Typography>
            <Typography>Have you already fixed the issue manually?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmId(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="warning"
              disabled={resolvingId === confirmId}
              onClick={() => handleResolve(confirmId)}
            >
              {resolvingId === confirmId ? 'Resolving…' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

export default ShopExceptionsDialog;
